// KnowledgeBank — the relations lens: each fact as a two-door sentence.
//
// A KB triple is three ids and a relation id; a lay author must never see any
// of them (view-agent-inspection.md §4, the no-raw-id rule). So each triple
// renders as a sentence — subject title —relation phrase→ object title — where
// the subject and object are DOORS (click to open that entity's tab) and the
// relation is a readable phrase (resolved by the shell's i18n relation
// dictionary, raw name as fallback).
//
// Two honesty markers ride each row:
//   • asserted vs INFERRED — a fact the agent was told reads plainly; one the
//     transitive closure worked out carries an "inferred" marker and, on
//     expand, shows the chain it was inferred through (the analogue of
//     grounded-vs-weak). A dangling ref shows its last-known title with a quiet
//     "removed" note, never an id.
//   • provenance — who stood the fact up: you, or the assistant (proposed and
//     approved). One calm marker, never per-edge noise.
//
// Pure/props-driven: every id arrives resolved; the doors reach out via props.

export { KnowledgeBank };

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../../Badge/Badge.js";
import { EmptyState } from "../../EmptyState/EmptyState.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import { ActorChip } from "../actor.js";
import type { KbTriple } from "../types.js";
import "./Inspection.css";

type KnowledgeBankProps = {
    readonly triples: readonly KbTriple[];
    // Open an entity's tab from either door. `key` is the opaque routing handle
    // the shell resolves; absent handler ⇒ the ends render as static titles.
    readonly onOpenEntity?: (key: string) => void;
};

const KnowledgeBank = ({
    triples,
    onOpenEntity,
}: KnowledgeBankProps): ReactNode => {
    if (triples.length === 0) {
        return (
            <EmptyState
                icon={<Icon name="database" size="lg" />}
                title={<Trans>Nothing connected yet</Trans>}
                body={
                    <Trans>
                        As you and the assistant note how concepts relate, those
                        facts appear here — each one traceable to where it came
                        from.
                    </Trans>
                }
            />
        );
    }
    return (
        <ul className="bilda-hi-kb">
            {triples.map((triple) => (
                <TripleRow
                    key={triple.id}
                    triple={triple}
                    onOpenEntity={onOpenEntity}
                />
            ))}
        </ul>
    );
};

// One triple as a sentence with two doors + its honesty markers.
const TripleRow = ({
    triple,
    onOpenEntity,
}: {
    triple: KbTriple;
    onOpenEntity?: (key: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <li className="bilda-hi-kb__row">
            <p className="bilda-hi-kb__sentence">
                <EntityDoor
                    title={triple.subjectTitle}
                    entityKey={triple.subjectKey}
                    removed={triple.subjectRemoved === true}
                    onOpenEntity={onOpenEntity}
                    removedLabel={t`removed`}
                />
                <span className="bilda-hi-kb__relation">
                    {triple.relationPhrase}
                </span>
                <EntityDoor
                    title={triple.objectTitle}
                    entityKey={triple.objectKey}
                    removed={triple.objectRemoved === true}
                    onOpenEntity={onOpenEntity}
                    removedLabel={t`removed`}
                />
            </p>
            <div className="bilda-hi-kb__markers">
                <ActorChip actor={triple.provenance} />
                {triple.inferred && (
                    <Badge
                        tone="neutral"
                        icon={<Icon name="sparkle" size="xs" />}
                    >
                        <Trans>Inferred</Trans>
                    </Badge>
                )}
            </div>
            {triple.inferred &&
                triple.chain !== undefined &&
                triple.chain.length > 0 && (
                    <details className="bilda-hi-kb__chain">
                        <summary className="bilda-hi-kb__chain-summary">
                            <Icon name="chevron-right" size="xs" />
                            <Trans>How this was inferred</Trans>
                        </summary>
                        <ol className="bilda-hi-kb__chain-list">
                            {triple.chain.map((step) => (
                                <li key={step}>
                                    <Text as="span" size="sm" tone="dim">
                                        {step}
                                    </Text>
                                </li>
                            ))}
                        </ol>
                    </details>
                )}
        </li>
    );
};

// One end of the sentence — a door when resolvable + a handler is given, else
// static. A removed ref shows its last-known title with a quiet "removed" note.
const EntityDoor = ({
    title,
    entityKey,
    removed,
    onOpenEntity,
    removedLabel,
}: {
    title: string;
    entityKey?: string;
    removed: boolean;
    onOpenEntity?: (key: string) => void;
    removedLabel: string;
}): ReactNode => {
    const inner =
        onOpenEntity && entityKey !== undefined && !removed ? (
            <button
                type="button"
                className="bilda-hi-kb__door"
                onClick={() => onOpenEntity(entityKey)}
            >
                {title}
            </button>
        ) : (
            <span className="bilda-hi-kb__entity">{title}</span>
        );
    return (
        <span className="bilda-hi-kb__end">
            {inner}
            {removed && (
                <Text as="span" size="xs" tone="faint">
                    {" "}
                    ({removedLabel})
                </Text>
            )}
        </span>
    );
};
